import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCloudUploadAlt,
  FaImage,
  FaTrash,
  FaPlus,
  FaTimes,
} from "react-icons/fa";

import "./HomePageImage.css";
import { BASE_URL } from "../../Config/config";

export default function HomepageImage() {
  const fileInputRef = useRef(null);

  // Change this according to your project
  const API_URL = `${BASE_URL}/homepage_images.php`;

  const IMAGE_URL = `${BASE_URL}/`;

  const [images, setImages] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* =====================================================
     GET IMAGES FUNCTION
  ===================================================== */

  const getHomepageImages = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}?action=get`);

      const result = await response.json();

      if (result.status) {
        setImages(result.data || []);
      } else {
        alert(result.message || "Failed to load images");
      }
    } catch (error) {
      console.error("Get Homepage Images Error:", error);

      alert("Unable to load homepage images.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });

    return () => {
      AOS.refresh();
    };
  }, []);
  /* =====================================================
     CALL GET FUNCTION
  ===================================================== */

  useEffect(() => {
    getHomepageImages();
  }, []);

  /* =====================================================
     OPEN UPLOAD
  ===================================================== */

  const openUpload = () => {
    setSelectedImage(null);
    setPreview("");
    setShowModal(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =====================================================
     CLOSE MODAL
  ===================================================== */

  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
    setPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =====================================================
     SELECT IMAGE
  ===================================================== */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image.");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return;
    }

    setSelectedImage(file);

    const imageUrl = URL.createObjectURL(file);

    setPreview(imageUrl);
  };

  /* =====================================================
     ADD IMAGE FUNCTION
  ===================================================== */

  const addHomepageImage = async () => {
    if (!selectedImage) {
      alert("Please select an image.");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();

      formData.append("image", selectedImage);

      const response = await fetch(`${API_URL}?action=add`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.status) {
        alert("Image uploaded successfully.");

        closeModal();

        getHomepageImages();
      } else {
        alert(result.message || "Failed to upload image.");
      }
    } catch (error) {
      console.error("Add Homepage Image Error:", error);

      alert("Something went wrong while uploading the image.");
    } finally {
      setUploading(false);
    }
  };

  const deleteHomepageImage = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?",
    );

    if (!confirmDelete) return;

    try {
      const formData = new FormData();

      formData.append("id", id);

      const response = await fetch(`${API_URL}?action=delete`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.status) {
        alert("Image deleted successfully.");

        // Call function again after delete
        getHomepageImages();
      } else {
        alert(result.message || "Failed to delete image.");
      }
    } catch (error) {
      console.error("Delete Homepage Image Error:", error);

      alert("Something went wrong while deleting the image.");
    }
  };

  /* =====================================================
     IMAGE URL
  ===================================================== */

  const getImageUrl = (image) => {
    if (!image) return "";

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${IMAGE_URL}${image}`;
  };

  return (
    <div className="homepage-image-page">
      <div
        className="homepage-image-header"
        data-aos="fade-down"
        data-aos-duration="800"
      >
        <div>
          <span
            className="homepage-image-label"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            HOMEPAGE MANAGEMENT
          </span>

          <h1 data-aos="fade-right" data-aos-delay="150">
            Homepage Images
          </h1>

          <p data-aos="fade-right" data-aos-delay="200">
            Manage the images displayed on the Star Computers homepage.
          </p>
        </div>

        <button
          type="button"
          className="add-image-btn"
          onClick={openUpload}
          data-aos="fade-left"
          data-aos-delay="200"
        >
          <FaPlus />
          Add Image
        </button>
      </div>

      {/* =================================================
          SUMMARY
      ================================================= */}

      <div className="image-summary" data-aos="fade-up" data-aos-delay="250">
        <div className="summary-card">
          <div className="summary-icon blue">
            <FaImage />
          </div>

          <div>
            <span>Total Images</span>
            <strong>{images.length}</strong>
          </div>
        </div>
      </div>
      {/* =================================================
          IMAGE CARD
      ================================================= */}
      <div
        className="homepage-image-card"
        data-aos="fade-up"
        data-aos-delay="300"
      >
        <div className="homepage-card-header">
          <div>
            <h2>Homepage Gallery</h2>
            <p>These images are displayed on your homepage.</p>
          </div>

          <button
            type="button"
            className="small-add-btn"
            onClick={openUpload}
            data-aos="fade-left"
            data-aos-delay="350"
          >
            <FaPlus />
            Add New
          </button>
        </div>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading && (
          <div className="empty-image-state">
            <FaImage />

            <h3>Loading Images...</h3>
          </div>
        )}

        {/* =================================================
            IMAGE GRID
        ================================================= */}

        {!loading && images.length > 0 && (
          <div className="homepage-image-grid">
            {images.map((item, index) => (
              <div
                className="homepage-image-item"
                key={item.id}
                data-aos="zoom-in"
                data-aos-delay={index * 100}
                data-aos-duration="600"
              >
                <div className="homepage-image-preview">
                  <img src={getImageUrl(item.image)} alt="Homepage" />
                </div>

                <div className="homepage-image-details">
                  <div className="image-actions">
                    <button
                      type="button"
                      className="delete-image-btn"
                      title="Delete"
                      onClick={() => deleteHomepageImage(item.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* =================================================
            EMPTY
        ================================================= */}

        {!loading && images.length === 0 && (
          <div
            className="empty-image-state"
            data-aos="fade-up"
            data-aos-duration="700"
          >
            <FaImage />

            <h3>No Homepage Images</h3>

            <p>Upload your first homepage image to get started.</p>

            <button
              type="button"
              onClick={openUpload}
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <FaPlus />
              Add Image
            </button>
          </div>
        )}
      </div>

      {/* =================================================
          UPLOAD MODAL
      ================================================= */}

      {showModal && (
        <div className="image-modal-overlay">
          <div
            className="image-modal"
            data-aos="zoom-in"
            data-aos-duration="400"
          >
            {/* HEADER */}

            <div className="image-modal-header">
              <div>
                <h2>Add Homepage Image</h2>

                <p>Upload an image for your homepage.</p>
              </div>

              <button
                type="button"
                className="modal-close-btn"
                onClick={closeModal}
              >
                <FaTimes />
              </button>
            </div>

            {/* BODY */}

            <div className="image-modal-body">
              <div
                className="image-upload-area"
                onClick={() => fileInputRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="upload-preview" />
                ) : (
                  <>
                    <div className="upload-icon">
                      <FaCloudUploadAlt />
                    </div>

                    <h3>Upload Homepage Image</h3>

                    <p>Click here to select an image</p>

                    <span>JPG, JPEG, PNG or WEBP</span>
                  </>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  hidden
                />
              </div>
            </div>

            {/* FOOTER */}

            <div className="image-modal-footer">
              <button
                type="button"
                className="cancel-image-btn"
                onClick={closeModal}
                disabled={uploading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="save-image-btn"
                onClick={addHomepageImage}
                disabled={uploading}
              >
                <FaCloudUploadAlt />

                {uploading ? "Uploading..." : "Save Image"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
